// ============================================================================
// ModuleHeader — header unificado para TODOS los módulos.
//
// Spec del handoff "Header estándar" (1-jun-2026, chat vz8zMcKZf_GcZpSRTal94w):
//
//   Desktop (4 filas, todas opcionales según el módulo):
//     ┌─────────────────────────────────────────────────────────────────────┐
//     │  [icono] Título  [LIVE]  │  N1 N2 N3 ...  │  ▸ Agregar · Recargar  │   ← fila 1
//     │  🔍  Búsqueda (ancho completo)                                       │   ← fila 2
//     │  [Tabs si existen]                                                   │
//     │  [Filtros primarios segmentados]  · · · · ·  [Toggle de vista]       │   ← fila 3
//     │  [Filtros secundarios]                       [Ordenar ▾]             │   ← fila 4
//     └─────────────────────────────────────────────────────────────────────┘
//
//   Mobile (<640px):
//     · Sin estadísticas.
//     · Acciones convertidas a íconos (sin texto).
//     · Búsqueda barra completa.
//     · Filtros con scroll horizontal (componente ChipFilter ya soporta).
//
// "La búsqueda, el botón de agregar y recargar caen siempre en el mismo lugar."
// El componente es data-driven: los módulos pasan props, no JSX, para que el
// orden y el estilo queden idénticos en toda la app.
//
// Backward-compat: se mantienen `actions` (slot raw) y `below` (slot raw) para
// los módulos legacy que aún no migraron a la API estructurada.
// ============================================================================
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Plus, RefreshCw, Search as SearchIcon, Wifi, ChevronDown, ArrowUpDown, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ChipFilter, type ChipOption } from './chip-filter';
import { useTextos } from '../../context/regional-context';
import { textosComun } from '../../../i18n/modulos/comun';

// ---------------- Tipos ---------------------------------------------------

export interface ModuleStat {
  n: number | string;
  label: string;
  tone?: 'ok' | 'blue' | 'warn' | 'muted' | 'danger';
}

/** Variante visual de los botones de acción del header. */
type ActionVariant = 'primary' | 'ghost' | 'dark';

export interface HeaderAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: ActionVariant;
  disabled?: boolean;
  /** En mobile el label se reemplaza por sólo ícono. Si quieres forzar texto, pon true. */
  keepLabelOnMobile?: boolean;
  /** Si quieres que el ícono haga spin (ej. Recargar loading). */
  spinning?: boolean;
  title?: string;
}

export interface HeaderSearch {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Click en X borra el query. Opcional. */
  showClear?: boolean;
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  count?: number;
  /** Color del dot a la izquierda del label cuando NO está activo. */
  tint?: ChipOption<string>['tint'];
  /** Tooltip nativo que se muestra al pasar el cursor sobre el chip. */
  title?: string;
}

export interface HeaderSegment<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: SegmentOption<T>[];
  /** Etiqueta accesible. */
  ariaLabel?: string;
}

export interface HeaderViewToggle<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; icon?: LucideIcon }[];
  ariaLabel?: string;
}

export interface HeaderSelect<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; group?: string }[];
  icon?: LucideIcon;
  /** Etiqueta antes del valor (ej. "Periodo:"). */
  prefix?: string;
}

export interface HeaderSort<T extends string> {
  value: T;
  onChange: (v: T) => void;
  /** Si la opción trae icon, el chip muestra el icono + tooltip con el label
      (ahorra mucho espacio horizontal). Si NO trae icon, muestra el label
      como antes (back-compat). */
  options: { value: T; label: string; icon?: LucideIcon }[];
  label?: string; // "Ordenar" por defecto
}

export interface HeaderLegendItem {
  label: string;
  color: string;
}

interface ModuleHeaderProps {
  // ===== Fila 1 ==========
  title: ReactNode;
  icon?: LucideIcon;
  live?: boolean;
  stats?: ModuleStat[];

  // Acciones derecha de fila 1 (forma estructurada, recomendada):
  onAdd?: () => void;
  addLabel?: string;
  addIcon?: LucideIcon; // default Plus
  addDisabled?: boolean;
  onRefresh?: () => void;
  refreshLoading?: boolean;
  /** Acciones extra (modos como Mesas Servicio/Editar plano, AdminPanel, etc.) */
  extraActions?: HeaderAction[];

  // Backward-compat: slot crudo a la derecha. Si se pasa, se usa en vez del bloque estructurado.
  actions?: ReactNode;

  // ===== Fila 2 — búsqueda =====
  search?: HeaderSearch;

  // ===== Tabs opcionales entre búsqueda y filtros =====
  tabs?: HeaderSegment<string>;

  // ===== Fila 3 — filtros primarios + view toggle =====
  primaryFilters?: HeaderSegment<string>;
  legend?: HeaderLegendItem[];
  viewToggle?: HeaderViewToggle<string>;

  // ===== Fila 4 — filtros secundarios / selects / ordenar =====
  secondaryFilters?: HeaderSegment<string>;
  selects?: HeaderSelect<string>[];
  sort?: HeaderSort<string>;

  // Slot crudo para algo que no encaje en los rieles definidos.
  below?: ReactNode;

  /** Línea divisora horizontal al pie del header. Default true. */
  divider?: boolean;
}

// ---------------- Helpers ----------------------------------------------------

const toneColor = (tone?: ModuleStat['tone']) => {
  switch (tone) {
    case 'ok':     return 'var(--ok)';
    case 'blue':   return 'var(--blue-600)';
    case 'warn':   return 'var(--warn-ink)';
    case 'muted':  return 'var(--muted-ink)';
    case 'danger': return 'var(--danger, #c0392b)';
    default:       return 'var(--ink)';
  }
};

function variantClass(v?: ActionVariant) {
  switch (v) {
    case 'primary': return 'sk-btn sk-btn--primary';
    case 'dark':    return 'sk-btn sk-btn--dark';
    case 'ghost':
    default:        return 'sk-btn sk-btn--ghost';
  }
}

/**
 * ActionButton — Botón del header que se degrada a sólo-ícono en mobile.
 * Renderiza el label oculto vía clase `hidden sm:inline` para que el botón se
 * quede compacto (~44x44) en pantallas chicas sin perder accesibilidad.
 */
function ActionButton({ a }: { a: HeaderAction }) {
  const Icon = a.icon;
  return (
    <button
      onClick={a.onClick}
      disabled={a.disabled}
      title={a.title || a.label}
      aria-label={a.label}
      className={`${variantClass(a.variant)} ${a.disabled ? 'opacity-60' : ''}`}
      style={{ minHeight: 44, padding: '10px 14px', fontSize: 14}}
    >
      {Icon && (
        <Icon
          className={`w-[18px] h-[18px] ${a.spinning ? 'animate-spin' : ''}`}
        />
      )}
      {a.label && (
        <span className={a.keepLabelOnMobile ? '' : 'hidden sm:inline'}>
          {a.label}
        </span>
      )}
    </button>
  );
}

// Convierte una HeaderSegment al shape que espera ChipFilter.
function toChipOptions<T extends string>(seg: HeaderSegment<T>): ChipOption<T>[] {
  return seg.options.map(o => ({
    value: o.value,
    label: o.label,
    count: o.count,
    tint: o.tint ?? 'default',
    title: o.title,
  }));
}

// ---------------- Componente -------------------------------------------------

export function ModuleHeader(props: ModuleHeaderProps) {
  const {
    title, icon: TitleIcon, live, stats,
    onAdd, addLabel, addIcon, addDisabled, onRefresh, refreshLoading, extraActions,
    actions,
    search, tabs, primaryFilters, legend, viewToggle, secondaryFilters, selects, sort,
    below,
    divider = true,
  } = props;
  const tx = useTextos(textosComun);

  const hasStats = Array.isArray(stats) && stats.length > 0;
  const AddIcon = addIcon ?? Plus;

  // Toggle de la barra de búsqueda dentro del módulo. Si el módulo ya tenía
  // texto en el query (ej. al volver de otra pantalla), arrancamos abierto
  // para que el filtro siga visible. Re-spec 2-jun-2026 (chat usuario): el
  // botón vive al lado de "Recargar" y al hacer click despliega la barra
  // bajo el título — antes era una barra gigante siempre visible.
  const [searchOpen, setSearchOpen] = useState<boolean>(() => !!search?.value);
  // Si el padre setea un query desde fuera (deep-link, etc), abrir la barra.
  useEffect(() => {
    if (search?.value) setSearchOpen(true);
  }, [search?.value]);

  // ----- Bloque de acciones estructurado --------------
  // Si se pasó `actions` (slot raw) usamos ESO. Si no, armamos las acciones
  // canónicas: extras → Agregar → Buscar → Recargar (orden invariante).
  // El botón "Buscar" comparte estilo ghost con "Recargar" y queda a su lado.
  const renderActions = (): ReactNode => {
    if (actions) return actions;
    const items: ReactNode[] = [];
    if (extraActions && extraActions.length > 0) {
      extraActions.forEach((a, i) => items.push(<ActionButton key={`ex-${i}`} a={a} />));
    }
    if (onAdd) {
      items.push(
        <ActionButton
          key="add"
          a={{
            label: addLabel ?? tx('nuevo'),
            icon: AddIcon,
            onClick: onAdd,
            variant: 'primary',
            disabled: addDisabled,
          }}
        />
      );
    }
    if (search) {
      items.push(
        <ActionButton
          key="search-toggle"
          a={{
            label: tx('buscar'),
            icon: SearchIcon,
            onClick: () => setSearchOpen(o => !o),
            variant: 'ghost',
            title: searchOpen ? tx('ocultarBusqueda') : tx('buscarEnModulo'),
          }}
        />
      );
    }
    if (onRefresh) {
      items.push(
        <ActionButton
          key="refresh"
          a={{
            label: tx('recargar'),
            icon: RefreshCw,
            onClick: onRefresh,
            variant: 'ghost',
            spinning: refreshLoading,
            title: tx('recargar'),
          }}
        />
      );
    }
    return items;
  };

  const hasRow3 = !!(primaryFilters || viewToggle || sort || (legend && legend.length > 0));
  const hasRow4 = !!(secondaryFilters || (selects && selects.length > 0));

  // Refactor jun-2026 (chat usuario "paddings equidistantes"): la cabecera
  // ahora es un flex column con padding uniforme alrededor y un `gap`
  // constante entre filas (en vez de cada fila trayendo su propio
  // padding-bottom distinto, que daba espaciados irregulares). Las filas
  // internas pierden su padding-bottom — el contenedor lo aporta todo.
  return (
    <div
      className="module-header"
      style={{
        borderBottom: divider ? '1.5px solid var(--line)' : undefined,
        marginBottom: divider ? 18 : 0,
        padding: 'clamp(18px, 2.4vw, 22px) clamp(18px, 3vw, 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* ===================== FILA 1 — TÍTULO + ACCIONES =====================
           Re-spec 2-jun-2026 (chat usuario): título solo, acciones a la derecha;
           las estadísticas se bajaron a su propia fila para tomar el full-width
           y no comprimir/empujar los botones cuando los números son largos. */}
      <div
        className="flex items-center flex-wrap"
        style={{
          columnGap: 'clamp(14px, 2vw, 24px)',
          rowGap: 12,
        }}
      >
        <h1
          className="m-0 tracking-tight flex items-center gap-2 sm:gap-2.5 flex-wrap"
          style={{
            color: 'var(--ink)',
            // En mobile bajamos el min a 20px y permitimos word-wrap del
            // título (entidades legales, contabilidad…) en vez de truncarlo
            // con ellipsis — pedido usuario jun-2026.
            fontSize: 'clamp(20px, 4vw, 36px)',
            fontWeight: 800,
            minWidth: 0,
            flex: '1 1 auto',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere' as const,
            lineHeight: 1.1,
          }}
        >
          {TitleIcon && (
            <TitleIcon
              className="w-[26px] h-[26px] sm:w-[28px] sm:h-[28px] flex-shrink-0"
              style={{ color: 'var(--blue-600)' }}
              strokeWidth={2}
            />
          )}
          {title}
          {live && (
            <span
              className="inline-flex items-center gap-1"
              style={{
                background: 'var(--ok-bg)', color: 'var(--ok-ink)',
                borderRadius: 'var(--r-pill)',
                fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
                padding: '4px 10px',
              }}
            >
              <Wifi className="w-3 h-3" /> {tx('enVivo')}
            </span>
          )}
        </h1>

        <div className="module-actions flex items-center flex-shrink-0" style={{ gap: 8, flexWrap: 'wrap' }}>
          {renderActions()}
        </div>
      </div>

      {/* ===================== FILA 1.5 — ESTADÍSTICAS =====================
           Re-spec 2-jun-2026 (screenshot Compras): contenedor pill rounded
           con borde y fondo sutil; ancho auto (no full-width). Cada stat
           separado por una línea vertical fina. Es un bloque "card" en lugar
           de una tira de stats sueltos. Si en pantallas chicas no cabe, hace
           scroll-x interno. */}
      {/* Stats: solo desktop (≥lg=1024px). En tablet y mobile las ocultamos
           porque el módulo KPIs ya da esa info global, y aquí ganamos espacio
           para el contenido del módulo. */}
      {hasStats && (
        <div className="hidden lg:block">
          <div
            className="module-header-stats inline-flex items-stretch"
            style={{
              maxWidth: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch' as const,
              scrollbarWidth: 'none' as const,
              background: 'var(--surface)',
              border: '1.5px solid var(--line)',
              borderRadius: 'var(--r-lg)',
              padding: '14px 6px',
              boxShadow: 'var(--sh-sm)',
            }}
          >
            {stats!.map((s, i) => (
              <div
                key={i}
                className="flex flex-col justify-center"
                style={{
                  padding: '0 22px',
                  position: 'relative',
                  lineHeight: 1,
                  borderLeft: i > 0 ? '1.5px solid var(--line)' : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <b
                  className="tabular-nums"
                  style={{
                    fontSize: 22, fontWeight: 800, color: toneColor(s.tone),
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.n}
                </b>
                <span style={{
                  fontSize: 12.5, fontWeight: 700, color: 'var(--muted-ink)',
                  marginTop: 6, letterSpacing: '.02em',
                  whiteSpace: 'nowrap',
                }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===================== FILA 2 — BÚSQUEDA (toggle) =====================
           Solo se renderiza si el usuario presionó el botón "Buscar" en las
           acciones de fila 1 (o si ya hay query persistido). Se despliega
           con animación suave debajo del título. */}
      {search && searchOpen && (
        <HeaderSearchBar
          search={search}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {/* ===================== TABS ===================== */}
      {tabs && (
        <div
          className="overflow-x-auto"
          style={{
            borderBottom: '1.5px solid var(--line)',
          }}
        >
          <nav className="flex gap-1 min-w-max" role="tablist" aria-label={tabs.ariaLabel || tx('pestanas')}>
            {tabs.options.map(t => {
              const active = tabs.value === t.value;
              return (
                <button
                  key={t.value}
                  role="tab"
                  aria-selected={active}
                  onClick={() => tabs.onChange(t.value)}
                  className="press whitespace-nowrap tracking-tight"
                  style={{
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 0,
                    borderBottom: active ? '3px solid var(--blue-600)' : '3px solid transparent',
                    color: active ? 'var(--blue-600)' : 'var(--muted-ink)',
                    fontSize: 14.5,
                    fontWeight: active ? 800 : 700,
                    cursor: 'pointer',
                    transition: 'color .15s, border-color .15s',
                  }}
                >
                  {t.label}
                  {typeof t.count === 'number' && (
                    <span className="ml-2 tabular-nums" style={{
                      padding: '2px 8px',
                      background: active ? 'var(--blue-100)' : 'var(--bg-app)',
                      color: active ? 'var(--blue-700)' : 'var(--muted-ink)',
                      borderRadius: 'var(--r-pill)',
                      fontSize: 12, fontWeight: 800,
                    }}>{t.count}</span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* ===================== FILA 3 — Filtros primarios + view toggle + sort =====================
           Re-spec 2-jun-2026: el sort pasa de fila 4 a esta misma línea, al
           final, junto a viewToggle. Eso lo deja "al final de la línea de
           materias primas y elaborados" en inventario, o al lado de los chips
           de tipo de egreso en compras — el componente queda chiquito y con
           acento amarillo para destacar sin pelear con los filtros principales. */}
      {hasRow3 && (
        <div
          className="flex items-center flex-wrap"
          style={{ gap: 10 }}
        >
          {primaryFilters && (
            <ChipFilter
              value={primaryFilters.value as any}
              onChange={primaryFilters.onChange as any}
              options={toChipOptions(primaryFilters)}
            />
          )}
          {legend && legend.length > 0 && (
            <div className="hidden sm:inline-flex items-center" style={{ gap: 12, paddingLeft: 6 }}>
              {legend.map((l, i) => (
                <span key={i} className="inline-flex items-center" style={{
                  gap: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--muted-ink)',
                }}>
                  <i style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: l.color, display: 'inline-block',
                  }} />
                  {l.label}
                </span>
              ))}
            </div>
          )}
          <div style={{ flex: 1 }} />
          {viewToggle && (
            <ChipFilter
              value={viewToggle.value as any}
              onChange={viewToggle.onChange as any}
              options={viewToggle.options.map(o => ({ value: o.value, label: o.label, tint: 'default' as const }))}
            />
          )}
          {sort && <HeaderSortPill sort={sort} />}
        </div>
      )}

      {/* ===================== FILA 4 — Filtros secundarios / selects ===================== */}
      {hasRow4 && (
        <div
          className="flex items-center flex-wrap"
          style={{ gap: 10 }}
        >
          {secondaryFilters && (
            <ChipFilter
              value={secondaryFilters.value as any}
              onChange={secondaryFilters.onChange as any}
              options={toChipOptions(secondaryFilters)}
            />
          )}
          {selects && selects.map((s, i) => (
            <HeaderSelectPill key={i} sel={s} />
          ))}
        </div>
      )}

      {below && <div>{below}</div>}
    </div>
  );
}

// ---------- Pildoritas auxiliares -------------------------------------------

// HeaderSearchBar — barra de búsqueda dentro del módulo. El toggle vive en
// fila 1 (botón "Buscar" estilo ghost junto a "Recargar"); este componente
// solo renderiza la barra ya expandida. Auto-focus al montar, Esc cierra
// (limpia primero si hay texto). Re-spec 2-jun-2026 (chat usuario).
function HeaderSearchBar({
  search,
  onClose,
}: {
  search: HeaderSearch;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const tx = useTextos(textosComun);

  // Auto-focus al montarse (cuando el toggle la abre).
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (search.value) search.onChange('');
      else onClose();
    }
  };

  return (
    <div
      className="flex items-center animate-apple-slide-down"
      style={{
        gap: 8, padding: '6px 8px 6px 14px', minHeight: 44,
        background: 'var(--surface)',
        border: '1.5px solid var(--line-2)',
        borderRadius: 'var(--r-pill)',
        boxShadow: 'var(--sh-sm)',
        width: '100%',
      }}
    >
      <SearchIcon
        className="w-[18px] h-[18px] flex-shrink-0"
        style={{ color: 'var(--muted-2)' }}
        strokeWidth={2.2}
      />
      <input
        ref={inputRef}
        type="text"
        value={search.value}
        onChange={(e) => search.onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={search.placeholder || tx('buscarPuntos')}
        aria-label={search.placeholder || tx('buscar')}
        className="flex-1 focus:outline-none"
        style={{
          border: 0, background: 'transparent',
          fontFamily: 'inherit', fontSize: 14.5, fontWeight: 600,
          color: 'var(--ink)',
          minWidth: 0,
        }}
      />
      {search.showClear !== false && search.value && (
        <button
          type="button"
          onClick={() => { search.onChange(''); inputRef.current?.focus(); }}
          aria-label={tx('limpiarBusqueda')}
          className="press"
          style={{
            padding: 6, borderRadius: '50%',
            background: 'var(--bg-app)', color: 'var(--muted-2)',
            border: 0, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label={tx('cerrarBusqueda')}
        title={tx('cerrarBusqueda')}
        className="press"
        style={{
          padding: 6, borderRadius: '50%',
          background: 'transparent', color: 'var(--muted-ink)',
          border: 0, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}


function HeaderSelectPill({ sel }: { sel: HeaderSelect<string> }) {
  const Icon = sel.icon;
  // Agrupado por `group` si los options lo traen.
  const grouped = sel.options.some(o => o.group);
  return (
    <label
      className="header-pillselect inline-flex items-center"
      style={{
        gap: 9, padding: '9px 14px', minHeight: 42,
        background: 'var(--surface)',
        border: '1.5px solid var(--line-2)',
        borderRadius: 'var(--r-pill)',
        boxShadow: 'var(--sh-sm)',
        fontSize: 13.5, fontWeight: 700, color: 'var(--ink)',
        cursor: 'pointer', position: 'relative',
      }}
    >
      {Icon && <Icon className="w-[15px] h-[15px]" style={{ color: 'var(--muted)' }} strokeWidth={2.2} />}
      {sel.prefix && <span style={{ color: 'var(--muted)' }}>{sel.prefix}</span>}
      <select
        value={sel.value}
        onChange={(e) => sel.onChange(e.target.value)}
        style={{
          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
          border: 0, background: 'transparent', cursor: 'pointer',
          fontFamily: 'inherit', fontWeight: 700, fontSize: 13.5, color: 'var(--ink)',
          paddingRight: 18, outline: 'none',
        }}
      >
        {grouped ? (
          Array.from(new Set(sel.options.map(o => o.group || ''))).map(g => (
            <optgroup key={g} label={g || ''}>
              {sel.options.filter(o => (o.group || '') === g).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </optgroup>
          ))
        ) : (
          sel.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
        )}
      </select>
      <ChevronDown className="w-[14px] h-[14px]" style={{ color: 'var(--muted)', marginLeft: -14, pointerEvents: 'none' }} />
    </label>
  );
}

// HeaderSortPill — botón compacto "Ordenar" que abre un popover con las
// opciones. Mobile: solo icon; sm+: icon + texto "Ordenar". El popover se
// renderiza en un portal al `document.body` con coords calculadas, así
// nunca queda escondido por `overflow: hidden` del module-header.
function HeaderSortPill({ sort }: { sort: HeaderSort<string> }) {
  const tx = useTextos(textosComun);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Calcula la posición del menu basada en el botón. Lo alineamos a la
  // derecha del botón y debajo. Si no cabe a la derecha del viewport, lo
  // empujamos hacia la izquierda.
  const computePos = () => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const W = 240;
    const margin = 8;
    const left = Math.max(margin, Math.min(window.innerWidth - W - margin, b.right - W));
    setMenuPos({ top: b.bottom + 6, left, width: W });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const onResize = () => computePos();
    const onScroll = () => computePos();
    const onClick = (e: MouseEvent) => {
      // El menu está en portal; cerramos si el click cae fuera del botón
      // y de cualquier elemento marcado con data-sort-menu.
      const t = e.target as HTMLElement;
      if (wrapRef.current?.contains(t)) return;
      if (t.closest?.('[data-sort-menu]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const activeOpt = sort.options.find(o => o.value === sort.value);
  const ActiveIcon = activeOpt?.icon;

  return (
    <div
      ref={wrapRef}
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={`${sort.label || tx('ordenarPor')}: ${activeOpt?.label || ''}`}
        className="press"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '8px 12px', minHeight: 38,
          background: open ? 'var(--surface)' : 'var(--bg-app)',
          border: '1.5px solid var(--line-2)',
          borderRadius: 999,
          fontFamily: 'inherit',
          fontSize: 13.5, fontWeight: 700,
          color: 'var(--ink)',
          boxShadow: open ? 'var(--sh-sm)' : 'none',
          cursor: 'pointer',
          transition: 'background .14s, box-shadow .14s, border-color .14s',
        }}
      >
        <ArrowUpDown className="w-[15px] h-[15px]" strokeWidth={2.2} style={{ color: 'var(--muted-ink)' }} />
        {/* Mobile: solo icono. Desde sm+: el texto "Ordenar". */}
        <span className="hidden sm:inline">{tx('ordenar')}</span>
        {ActiveIcon && (
          <ActiveIcon
            className="w-[15px] h-[15px]"
            strokeWidth={2.2}
            style={{ color: 'var(--blue-600)' }}
          />
        )}
        <ChevronDown className="w-[13px] h-[13px]" strokeWidth={2.4} style={{ color: 'var(--muted-2)' }} />
      </button>

      {open && menuPos && typeof document !== 'undefined' && createPortal(
        <div
          data-sort-menu
          role="menu"
          aria-label={sort.label || tx('ordenarPor')}
          style={{
            position: 'fixed',
            top: menuPos.top, left: menuPos.left, width: menuPos.width,
            maxWidth: 'calc(100vw - 16px)',
            background: 'var(--surface)',
            border: '1.5px solid var(--line)',
            borderRadius: 14,
            boxShadow: 'var(--sh-lg)',
            padding: 6,
            zIndex: 9999,
          }}
        >
          <div style={{
            padding: '6px 10px 4px',
            fontSize: 10.5, fontWeight: 800, letterSpacing: '.1em',
            color: 'var(--muted-2)', textTransform: 'uppercase',
          }}>
            {sort.label || tx('ordenarPor')}
          </div>
          {sort.options.map(o => {
            const isActive = o.value === sort.value;
            const Icon = o.icon;
            return (
              <button
                key={o.value}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => { sort.onChange(o.value); setOpen(false); }}
                className="press"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 10,
                  border: 0, cursor: 'pointer',
                  background: isActive ? 'var(--blue-50)' : 'transparent',
                  color: isActive ? 'var(--blue-700)' : 'var(--ink)',
                  fontFamily: 'inherit', fontSize: 13.5, fontWeight: isActive ? 800 : 600,
                  textAlign: 'left',
                  transition: 'background .12s',
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'var(--bg-app)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = 'transparent';
                }}
              >
                {Icon && <Icon className="w-[15px] h-[15px]" strokeWidth={2.2} style={{ color: isActive ? 'var(--blue-600)' : 'var(--muted-ink)' }} />}
                <span style={{ flex: 1 }}>{o.label}</span>
                {isActive && <Check className="w-[15px] h-[15px]" strokeWidth={2.6} />}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
